/**
 * GeoDirectory Frontend Script
 * Handles frontend interactions.
 * Relies on window.Alpine being available (loaded via Composer package).
 */

import { initBusinessHoursDisplay } from './frontend/business-hours.js';
import { initRatingInput } from './shared/rating-input.js';
import { toggleFavorite, initFavorites } from './frontend/favorites.js';
import { initLazyLoad, triggerLazyLoadCheck } from './frontend/lazy-load.js';
import { initTabsHashHandler } from './frontend/tabs.js';
import { initSearchForms, triggerSearch } from './frontend/search-form.js';
import { initSearchGeocoding } from './frontend/search-geocode.js';

// Initialize GeoDir namespace
window.GeoDir = window.GeoDir || {};

// Store cleanup functions for reinit
let cleanupFunctions = [];

/**
 * Initialize all GeoDirectory frontend components.
 * Call this after page load or after AJAX content is inserted.
 *
 * @param {HTMLElement|Document} context - Container to initialize within (default: document)
 */
window.GeoDir.init = function(context = document) {
	console.log('GeoDirectory: Initializing components', context);

	// Clean up previous initializations if reinitializing
	if (cleanupFunctions.length > 0) {
		cleanupFunctions.forEach(cleanup => cleanup());
		cleanupFunctions = [];
	}

	// Initialize lazy load
	const lazyCleanup = initLazyLoad();
	if (lazyCleanup) {
		cleanupFunctions.push(lazyCleanup);
	}

	// Initialize tabs hash handler (only on first load)
	if (context === document) {
		const tabsCleanup = initTabsHashHandler();
		if (tabsCleanup) {
			cleanupFunctions.push(tabsCleanup);
		}
	}

	// Initialize rating inputs
	initRatingInput();

	// Initialize favorites (only on first load)
	if (context === document) {
		initFavorites();
	}

	// Initialize business hours display
	const bhDisplay = initBusinessHoursDisplay();
	if (bhDisplay) {
		window.GeoDir.businessHoursDisplay = bhDisplay;
	}

	// Initialize search forms
	initSearchForms();

	// Initialize search geocoding
	initSearchGeocoding();
};

// Expose favorites globally for inline onclick handlers
window.geodirToggleFavorite = toggleFavorite;

// Expose lazy load trigger
window.GeoDir.triggerLazyLoadCheck = triggerLazyLoadCheck;

// Expose search trigger
window.GeoDir.triggerSearch = triggerSearch;

/**
 * REST API Client
 * Handles all API requests to GeoDirectory REST endpoints.
 */
window.GeoDir.api = {
	baseURL: window.geodir_params?.rest_url || '/wp-json/geodir/v3',
	nonce: window.geodir_params?.rest_nonce || '',

	/**
	 * Make an API request.
	 *
	 * @param {string} endpoint - API endpoint (e.g., 'places/123')
	 * @param {object} options - Fetch options
	 * @returns {Promise<object>} Response data
	 */
	async request(endpoint, options = {}) {
		const url = `${this.baseURL}/${endpoint}`;
		const headers = {
			'X-WP-Nonce': this.nonce,
			'Content-Type': 'application/json',
			...options.headers
		};

		try {
			const response = await fetch(url, { ...options, headers });
			const data = await response.json();

			if (!response.ok) {
				throw new Error(data.message || `HTTP ${response.status}: ${response.statusText}`);
			}

			return data;
		} catch (error) {
			console.error('GeoDir API Error:', error);
			throw error;
		}
	},

	/**
	 * POST request.
	 *
	 * @param {string} endpoint - API endpoint
	 * @param {object} body - Request body
	 * @returns {Promise<object>}
	 */
	async post(endpoint, body) {
		return this.request(endpoint, {
			method: 'POST',
			body: JSON.stringify(body)
		});
	},

	/**
	 * PUT request.
	 *
	 * @param {string} endpoint - API endpoint
	 * @param {object} body - Request body
	 * @returns {Promise<object>}
	 */
	async put(endpoint, body) {
		return this.request(endpoint, {
			method: 'PUT',
			body: JSON.stringify(body)
		});
	},

	/**
	 * DELETE request.
	 *
	 * @param {string} endpoint - API endpoint
	 * @returns {Promise<object>}
	 */
	async delete(endpoint) {
		return this.request(endpoint, {
			method: 'DELETE'
		});
	},

	/**
	 * Upload a file.
	 *
	 * @param {File} file - File to upload
	 * @param {number} postId - Optional post ID
	 * @returns {Promise<object>}
	 */
	async uploadFile(file, postId = 0) {
		const formData = new FormData();
		formData.append('file', file);
		if (postId) {
			formData.append('post_id', postId);
		}

		const url = `${this.baseURL}/media/upload`;
		const response = await fetch(url, {
			method: 'POST',
			headers: {
				'X-WP-Nonce': this.nonce
			},
			body: formData
		});

		const data = await response.json();
		if (!response.ok) {
			throw new Error(data.message || 'Upload failed');
		}

		return data;
	}
};

// Wait for Alpine to initialize
document.addEventListener('alpine:init', () => {
	console.log('GeoDirectory: Alpine initialized');

	// Example: Register a global Alpine data object or store here if needed
	// Alpine.data('geoDirSearch', () => ({ ... }));
});

// Standard DOM Ready for non-Alpine logic
document.addEventListener('DOMContentLoaded', () => {
	console.log('GeoDirectory: Frontend DOM loaded');

	// Initialize all components
	window.GeoDir.init();

	// Expose additional utilities
	window.GeoDir.toggleFavorite = toggleFavorite;
	window.GeoDir.initRatingInput = initRatingInput;
});

// Listen for custom event to reinitialize after AJAX content loads
document.addEventListener('geodir:contentLoaded', (e) => {
	console.log('GeoDirectory: Content loaded via AJAX, reinitializing');
	const context = e.detail?.container || document;
	window.GeoDir.init(context);
});

// Handle tab clicks to trigger lazy load checks and resize events
document.addEventListener('shown.bs.tab', (e) => {
	if (e.target.hash && document.querySelector(e.target.hash)) {
		setTimeout(() => {
			window.GeoDir.triggerLazyLoadCheck();
			// Trigger resize event for maps and other responsive components
			window.dispatchEvent(new Event('resize'));
		}, 100);
	}
});
